import { callClaude, ClaudeApiError } from './anthropic';
import {
  normalizeMeetingPractitionerNote,
  validateReviewedClinicalRecord,
} from './consultationMeetingWorkspace';
import { sha256Hex } from './journeyState';
import {
  buildFollowupSafetyProofV1,
  hashFollowupSafetySnapshotV1,
} from './followupSafetySnapshot';
import { buildFollowupNutritionVersion } from './nutritionConsultationPersistence';
import { projectActiveTreatments } from './clinical/currentTreatments';
import {
  assertFollowupAiPromptAnonymized,
  collectIdentityValues,
  redactDeep,
  redactText,
} from '../suivi/services/anonymiser';

export { assertFollowupAiPromptAnonymized };

export const REVIEWED_FOLLOWUP_NOTE_MAX_LENGTH = 2400;
export const REVIEWED_FOLLOWUP_UPDATE_SCOPES = Object.freeze([
  'targeted',
  'menus_recipes',
  'full',
]);

function resolveUpdateScope(value, workflowAction) {
  const scope = value;
  if (!scope) {
    throw new Error('Portée de mise à jour absente — à valider Anissa.');
  }
  if (!REVIEWED_FOLLOWUP_UPDATE_SCOPES.includes(scope)) {
    throw new Error('Portée de mise à jour invalide — à valider Anissa.');
  }
  if (scope === 'full' && workflowAction !== 'newVersion') {
    throw new Error('Le dernier compte rendu relu ne prévoit pas une nouvelle version complète.');
  }
  if (scope !== 'full' && workflowAction !== 'adapt') {
    throw new Error('Le dernier compte rendu relu prévoit une version complète, pas un ajustement ciblé.');
  }
  return scope;
}

function buildSafetyPromptDto(snapshot) {
  const activeTreatments = projectActiveTreatments(snapshot);
  return Object.freeze({
    allergies: snapshot.allergies,
    aliments_a_eviter: snapshot.alimentsEvites,
    traitements_medicaments: activeTreatments.text ? [activeTreatments.text] : [],
    restrictions_alimentaires: snapshot.restrictionsAlimentaires,
    restriction_autre: snapshot.restrictionsAutre,
    ramadan_actif: snapshot.ramadanActif,
    complements_actuels: snapshot.complementsActuels,
    presentation_sans_chiffres: snapshot.presentationSansChiffres,
  });
}

function buildAppointmentPromptDto(appointment) {
  return Object.freeze({
    summary: appointment.summary,
    period_label: appointment.period_label,
    questions: appointment.questions.map((question) => Object.freeze({
      text: question.text,
      source: question.source,
      answer: question.answer,
    })),
    practitioner_note: appointment.practitioner_note,
  });
}

function cleanNote(value) {
  if (typeof value !== 'string') return '';
  const normalized = normalizeMeetingPractitionerNote(value);
  if (normalized.length > REVIEWED_FOLLOWUP_NOTE_MAX_LENGTH) {
    throw new Error(`La note dépasse ${REVIEWED_FOLLOWUP_NOTE_MAX_LENGTH} caractères.`);
  }
  return normalized;
}

function normalizeSectionTitle(value) {
  return String(value ?? '').replace(/[*_`#]/g, '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr-CH');
}

export function buildPlanSectionManifest(plan) {
  if (typeof plan !== 'string' || !plan.trim()) throw new Error('Plan courant complet introuvable.');
  const sections = [];
  const preambleLines = [];
  let current = null;
  const flush = () => {
    if (!current) return;
    const content = current.lines.join('\n').trim();
    const meaningfulCharacters = content.match(/[\p{L}\p{N}]/gu)?.length || 0;
    const blockCount = content
      .split(/\n\s*\n/u)
      .filter((block) => /[\p{L}\p{N}]/u.test(block)).length;
    sections.push(Object.freeze({
      title: current.title,
      normalized_title: normalizeSectionTitle(current.title),
      meaningful_characters: meaningfulCharacters,
      block_count: blockCount,
      has_content: meaningfulCharacters > 0 && blockCount > 0,
    }));
  };
  for (const rawLine of plan.split('\n')) {
    const line = rawLine.trim();
    const markdown = line.match(/^#{1,6}\s+(.+)$/);
    const allCaps = !markdown && line.length > 5 && line.length < 120 && /[A-ZÀ-ÖØ-Þ]/.test(line) && line === line.toLocaleUpperCase('fr-CH');
    if (markdown || allCaps) {
      flush();
      current = { title: (markdown ? markdown[1] : line).trim(), lines: [] };
    } else if (current) {
      current.lines.push(rawLine);
    } else {
      preambleLines.push(rawLine);
    }
  }
  flush();
  if (/[\p{L}\p{N}]/u.test(preambleLines.join('\n'))) {
    throw new Error('À valider Anissa — le plan source contient un préambule hors manifeste ; relis sa structure avant l’adaptation.');
  }
  if (sections.length < 2 || sections.some((section) => !section.has_content)) {
    throw new Error('À valider Anissa — le plan source ne contient pas un manifeste de sections complet et vérifiable.');
  }
  const normalized = sections.map((section) => section.normalized_title);
  if (new Set(normalized).size !== normalized.length) {
    throw new Error('À valider Anissa — deux sections du plan portent le même titre.');
  }
  return Object.freeze(sections);
}

export function assertPlanMatchesSectionManifest(plan, manifest) {
  const actual = buildPlanSectionManifest(plan);
  if (
    !Array.isArray(manifest)
    || actual.length !== manifest.length
    || actual.some((section, index) => (
      section.normalized_title !== manifest[index]?.normalized_title
      || !section.has_content
      || section.meaningful_characters < manifest[index]?.meaningful_characters
      || section.block_count < manifest[index]?.block_count
    ))
  ) {
    throw new Error('La proposition ne restitue pas toutes les sections du plan source dans le même ordre.');
  }
  return true;
}

export async function assertReviewedFollowupSafety({ currentForm, safetySnapshot = null } = {}) {
  const current = await buildFollowupSafetyProofV1(currentForm);
  if (safetySnapshot) {
    const reviewedHash = await hashFollowupSafetySnapshotV1(safetySnapshot);
    if (reviewedHash !== current.sha256) {
      throw new Error('À valider Anissa — le snapshot de sécurité est périmé. Relis le dossier avant de continuer.');
    }
  }
  const maternal = [
    current.snapshot.grossesseActuelle,
    current.snapshot.allaitement,
    current.snapshot.postPartum,
  ].some((value) => value === 'Oui');
  if (maternal || current.snapshot.flagsProfilRisque.length > 0) {
    throw new Error('À valider Anissa — adaptation assistée fermée pour ce contexte maternel ou profil à risque.');
  }
  return current;
}

export async function buildReviewedFollowupAdaptationPrompt({
  currentPlan,
  currentForm,
  identityContext = null,
  safetySnapshot = null,
  appointment,
  clinical,
  note,
  clientId,
  sourceConsultationId,
  consultationOrdinal,
  updateScope = null,
} = {}) {
  if (typeof currentPlan !== 'string' || !currentPlan.trim()) throw new Error('Plan courant complet introuvable.');
  const safety = await assertReviewedFollowupSafety({ currentForm, safetySnapshot });
  const reviewedClinical = await validateReviewedClinicalRecord(clinical, {
    clientId,
    sourceConsultationId,
    consultationOrdinal,
  });
  if (appointment?.review_content_sha256 !== reviewedClinical.appointment.review_content_sha256) {
    throw new Error('Le compte rendu relu ne correspond pas aux observations — à valider Anissa.');
  }
  if (reviewedClinical.workflow_action !== 'adapt' && reviewedClinical.workflow_action !== 'newVersion') {
    throw new Error('Action d’adaptation absente — à valider Anissa.');
  }
  const exactUpdateScope = resolveUpdateScope(updateScope, reviewedClinical.workflow_action);
  if (cleanNote(note) !== reviewedClinical.appointment.practitioner_note) {
    throw new Error('La note ne correspond plus au compte rendu confirmé — à valider Anissa.');
  }
  const identityValues = collectIdentityValues(currentForm, identityContext);
  const redactedPlan = redactText(currentPlan, identityValues);
  const manifest = buildPlanSectionManifest(redactedPlan);
  const promptDto = {
    version: 2,
    safety_context: redactDeep(buildSafetyPromptDto(safety.snapshot), identityValues),
    required_section_manifest: manifest.map((section) => ({
      title: section.title,
      minimum_meaningful_characters: section.meaningful_characters,
      minimum_blocks: section.block_count,
    })),
    reviewed_appointment: redactDeep(buildAppointmentPromptDto(reviewedClinical.appointment), identityValues),
    practitioner_observations: redactDeep({
      workflow_action: reviewedClinical.workflow_action,
      update_scope: exactUpdateScope,
    }, identityValues),
    current_plan_complete: redactedPlan,
  };
  const serialized = JSON.stringify(promptDto);
  assertFollowupAiPromptAnonymized(serialized, identityValues);
  return serialized;
}

const ADAPTATION_SYSTEM_PROMPT = `Tu aides Anissa à préparer un brouillon de nouvelle version d'un plan nutritionnel déjà validé.

Tu reçois le plan courant complet anonymisé, les contraintes de sécurité utiles et uniquement un compte rendu relu et confirmé par Anissa.

Règles absolues :
- restitue toutes les sections du manifeste, dans le même ordre et avec exactement les mêmes titres ;
- produis un plan COMPLET et autonome, jamais un extrait, un résumé ou une liste de changements ;
- reste fidèle au plan source et ne modifie que ce que les observations confirmées justifient ;
- respecte la portée éditoriale demandée : « targeted » limite les changements aux éléments concernés, « menus_recipes » limite les changements aux menus et recettes, « full » autorise une révision complète justifiée par le compte rendu ;
- n'invente aucune donnée, aucun diagnostic, aucune dose, aucun traitement ni nouvelle contrainte clinique ;
- n'ajoute aucune identité ;
- la sortie reste un brouillon non publié qu'Anissa relira et pourra éditer.

Réponds uniquement avec le texte complet du plan proposé, sans préambule.`;

export async function proposeReviewedFollowupAdaptation(input = {}, callAi = callClaude) {
  const user = await buildReviewedFollowupAdaptationPrompt(input);
  const parsedInput = JSON.parse(user);
  let content;
  try {
    content = await callAi({ system: ADAPTATION_SYSTEM_PROMPT, user, maxTokens: 12000, sanitizeOutput: true });
  } catch (error) {
    if (error instanceof ClaudeApiError) throw new Error(`La proposition a échoué : ${error.message}`);
    throw new Error(`La proposition a échoué : ${error?.message || error}`);
  }
  const exactContent = typeof content === 'string' ? content.trim() : '';
  if (!exactContent) throw new Error("La préparation n’a renvoyé aucun plan complet.");
  const manifest = buildPlanSectionManifest(parsedInput.current_plan_complete);
  assertPlanMatchesSectionManifest(exactContent, manifest);
  const safety = await assertReviewedFollowupSafety(input);
  const reviewedClinical = await validateReviewedClinicalRecord(input.clinical, {
    clientId: input.clientId,
    sourceConsultationId: input.sourceConsultationId,
    consultationOrdinal: input.consultationOrdinal,
  });
  const updateScope = resolveUpdateScope(input.updateScope, reviewedClinical.workflow_action);
  return Object.freeze({
    version: 2,
    content: exactContent,
    source_plan_sha256: await sha256Hex(input.currentPlan),
    source_consultation_id: input.sourceConsultationId,
    safety_snapshot: safety.snapshot,
    safety_snapshot_sha256: safety.sha256,
    appointment_sha256: reviewedClinical.appointment.review_content_sha256,
    appointment_client_id: reviewedClinical.appointment.client_id,
    consultation_ordinal: reviewedClinical.appointment.consultation_ordinal,
    section_manifest: manifest,
    workflow_action: reviewedClinical.workflow_action,
    update_scope: updateScope,
    generated_at: new Date().toISOString(),
  });
}

export async function assertReviewedFollowupAdaptationReady({
  proposal,
  editedContent,
  confirmedContent,
  currentPlan,
  currentForm,
  sourceConsultationId,
  latestAppointment,
} = {}) {
  if (!proposal || proposal.version !== 2 || typeof editedContent !== 'string' || !editedContent.trim()) {
    throw new Error('Proposition d’adaptation absente.');
  }
  resolveUpdateScope(proposal.update_scope, proposal.workflow_action);
  if (confirmedContent !== editedContent) throw new Error('À valider Anissa — confirme le contenu exact après la dernière modification.');
  if (!sourceConsultationId || proposal.source_consultation_id !== sourceConsultationId) {
    throw new Error('Une version plus récente existe. Recharge avant de sauvegarder.');
  }
  if (await sha256Hex(currentPlan) !== proposal.source_plan_sha256) {
    throw new Error('Le plan source a changé. Prépare à nouveau l’adaptation.');
  }
  assertPlanMatchesSectionManifest(editedContent, proposal.section_manifest);
  const safety = await assertReviewedFollowupSafety({ currentForm, safetySnapshot: proposal.safety_snapshot });
  if (safety.sha256 !== proposal.safety_snapshot_sha256) {
    throw new Error('À valider Anissa — la preuve de sécurité ne correspond plus à la fiche.');
  }
  if (
    !latestAppointment
    || latestAppointment.review_content_sha256 !== proposal.appointment_sha256
    || latestAppointment.client_id !== proposal.appointment_client_id
    || latestAppointment.source_consultation_id !== proposal.source_consultation_id
    || latestAppointment.consultation_ordinal !== proposal.consultation_ordinal
  ) {
    throw new Error('Le dernier rendez-vous canonique ne correspond plus à cette adaptation — à valider Anissa.');
  }
  return editedContent;
}

export async function persistReviewedFollowupAdaptation({
  proposal,
  editedContent,
  confirmedContent,
  currentForm,
  sourceConsultation,
  latestAppointment,
  clientId,
  followupWeek,
  versionNumber,
  persist,
} = {}) {
  if (typeof persist !== 'function') throw new Error('Service de sauvegarde indisponible.');
  const currentPlan = sourceConsultation?.nutritionPlan || sourceConsultation?.nutrition_plan || sourceConsultation?.plan_text || '';
  const exactContent = await assertReviewedFollowupAdaptationReady({
    proposal,
    editedContent,
    confirmedContent,
    currentPlan,
    currentForm,
    sourceConsultationId: sourceConsultation?.id,
    latestAppointment,
  });
  const decisionLabel = proposal.workflow_action === 'newVersion' ? 'Nouvelle version relue' : 'Adaptation relue';
  const dateStr = new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: '2-digit' });
  const candidate = buildFollowupNutritionVersion(sourceConsultation, {
    nutritionPlan: exactContent,
    status: 'a_valider',
    label: `V${versionNumber} — ${decisionLabel} du ${dateStr}`,
    consultantName: 'Anissa',
  }, {
    clientId,
    sourceConsultationId: sourceConsultation.id,
    followupWeek,
    provenance: {
      trigger: 'reviewed_appointment_adaptation',
      workflowAction: proposal.workflow_action,
      updateScope: proposal.update_scope,
      appointmentSha256: proposal.appointment_sha256,
      safetySnapshotSha256: proposal.safety_snapshot_sha256,
      appointmentClientId: proposal.appointment_client_id,
      consultationOrdinal: proposal.consultation_ordinal,
    },
  });
  return persist(candidate);
}
